/**
 * ReviewExclusiveSubscriptionSheet
 *
 * Helper to open the creator-side review (approve/reject) bottom sheet for an
 * exclusive-content subscription request via SheetService. Mirrors the pattern
 * of the sibling `SubscribeUserSheet.tsx`.
 *
 * Usage:
 *   ReviewExclusiveSubscriptionSheet.show({
 *     requestNotificationId: notification.id,
 *     userId: notification.info.usersInfo.userId,
 *     screenKey,
 *     container,
 *   });
 *
 * Flutter: lib/services/navigation_service.dart -> showReviewExclusiveSubscriptionSheet()
 *
 * Sole caller: `handleExclusiveSubscriptionRequestedTap`
 * (`presentation/feature/inbox/notificationTap/handlers/userNavigationNotificationTapHandler.ts`),
 * which owns Flutter's `actioned` gate (`push_navigation_service.dart:99-100`)
 * exactly as Flutter does — in the tap dispatcher, not in the sheet.
 *
 * ## Params are fixed by that caller's contract
 *
 * Both fields are exactly what a notification tap can supply, with no extra
 * fetch: `requestNotificationId` = `AppNotificationEntity.id`
 * (Flutter `notification.id!`, `push_navigation_service.dart:107`) and
 * `userId` = `AppNotificationEntity.info.usersInfo.userId`
 * (Flutter `:108`). They are required, non-optional strings, matching Flutter's
 * non-nullable `String` params (`navigation_service.dart:2414-2415`); resolving
 * the optional `AppNotificationEntity.id?` is the caller's job, as in Flutter's
 * `notification.id!` null-assert.
 *
 * ## Known limitation (recorded, not worked around)
 *
 * Flutter blocks *every* dismissal path while the callable is in flight:
 * `_onBackPress` returns `false`
 * (`review_exclusive_subscription_bottom_sheet.dart:47-53`), `_closeBottomSheet`
 * early-returns (`:55-60`), and the `Positioned.fill` overlay absorbs taps
 * (`:231-251`). On web, `closeOnClickOutside` is fixed at show time and cannot
 * be toggled from sheet content, so the backdrop / ESC / drag paths stay
 * dismissible mid-flight while every control the sheet owns (Confirm / Reject /
 * Close) is disabled behind the loading overlay (Task 6). This is safe because
 * the client is thin: the Cloud Function completes server-side regardless of
 * whether the sheet is still mounted, and no client state depends on the
 * result. Do not add a custom non-dismissible surface for this.
 */
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService.index';
import { ReviewExclusiveSubscriptionSheetContent } from './ReviewExclusiveSubscriptionSheetContent';

export interface ReviewExclusiveSubscriptionSheetConfig {
  /** Id of the `exclusiveSubscriptionRequested` notification being reviewed. */
  requestNotificationId: string;
  /** Id of the requesting (peer) user whose access is being reviewed. */
  userId: string;
  screenKey?: string;
  container?: HTMLElement | string | null;
}

const show = (config: ReviewExclusiveSubscriptionSheetConfig): void => {
  SheetService.show({
    // Empty: the title is rendered reactively inside the content, which has the
    // `useLocale()` access this plain module function lacks (the
    // `UnlockContentSheet.tsx:22` precedent).
    title: '',
    content: (
      <ReviewExclusiveSubscriptionSheetContent
        requestNotificationId={config.requestNotificationId}
        userId={config.userId}
      />
    ),
    container: config.container,
    screenKey: config.screenKey,
    // Flutter's tap-outside-to-dismiss (`:342-347`, `topTransparentWidget`'s
    // `onTap: _closeBottomSheet`); matches both sibling sheets.
    closeOnClickOutside: true,
    // Renders `data-qa-id="sheet-review-exclusive-subscription"`
    // (`BottomSheetView.tsx:324`) so a UI test can `wait_for` this sheet.
    qaId: 'review-exclusive-subscription',
  });
};

export const ReviewExclusiveSubscriptionSheet = {
  show,
};
